import { Navigate, Route, Routes } from "react-router-dom";
import type { RouteConfig } from "../types/navigation";
import { AuthLayout } from "../components/layout/AuthLayout";
import { ProtectedRoute } from "../components/auth/ProtectedRoute";
import { RootRedirect } from "../components/auth/RootRedirect";
import { DashboardLayout } from "../components/layout";
import { SignInPage } from "../pages/SignInPage";
import { AnalyticsPage } from "../pages/AnalyticsPage";
import { UnauthorizedPage } from "../pages/UnauthorizedPage";
import { UserManagementPage } from "../pages/UserManagementPage";
import { LanguageManagementPage } from "../pages/LanguageManagementPage";
import { GenreManagementPage } from "../pages/GenreManagementPage";
import { CastManagementPage } from "../pages/CastManagementPage";
import { MovieManagementPage } from "../pages/movie/MovieManagementPage";
import { MovieCreatePage } from "../pages/movie/MovieCreatePage";
import { MovieEditPage } from "../pages/movie/MovieEditPage";
import { MovieDetailPage } from "../pages/movie/MovieDetailPage";
import { SeriesManagementPage } from "../pages/series/SeriesManagementPage";
import { SeriesCreatePage } from "../pages/series/SeriesCreatePage";
import { SeriesEditPage } from "../pages/series/SeriesEditPage";
import { SeriesDetailPage } from "../pages/series/SeriesDetailPage";
import { MusicVideoManagementPage } from "../pages/music-video/MusicVideoManagementPage";
import { AlbumManagementPage } from "../pages/album/AlbumManagementPage";
import { SongManagementPage } from "../pages/song/SongManagementPage";
import { ArtistManagementPage } from "../pages/artist/ArtistManagementPage";
import { PlaylistsUnavailablePage } from "../pages/playlist/PlaylistsUnavailablePage";
import { DocumentaryManagementPage } from "../pages/documentary/DocumentaryManagementPage";
import { DocumentaryCreatePage } from "../pages/documentary/DocumentaryCreatePage";
import { DocumentaryEditPage } from "../pages/documentary/DocumentaryEditPage";
import { DocumentaryDetailPage } from "../pages/documentary/DocumentaryDetailPage";
import { SubscriptionPlanManagementPage } from "../pages/subscription/SubscriptionPlanManagementPage";
import { BannerManagementPage } from "../pages/banner/BannerManagementPage";
import { TrailerManagementPage } from "../pages/trailer/TrailerManagementPage";
import { SplashScreenManagementPage } from "../pages/splash-screen/SplashScreenManagementPage";
import { PaymentMethodManagementPage } from "../pages/payment-method/PaymentMethodManagementPage";
import { TvUserOnboardingPage } from "../pages/admin/TvUserOnboardingPage";
import { SettingsPage } from "../pages/settings/SettingsPage";
import { AppUpdateManagementPage } from "../pages/admin/AppUpdateManagementPage";

/**
 * Protected Route Wrapper Component
 * Wraps routes with authentication and role-based access control
 * The DashboardLayout uses <Outlet /> to render nested child routes
 */
interface ProtectedLayoutProps {
  config: RouteConfig;
}

const ProtectedDashboardWrapper: React.FC<ProtectedLayoutProps> = ({ config }) => {
  return (
    <ProtectedRoute config={config}>
      <DashboardLayout />
    </ProtectedRoute>
  );
};

export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RootRedirect />} />

      {/* Authentication Routes with Auth Layout */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<SignInPage />} />
        {/* <Route path="register" element={<RegisterPage />} />
        <Route path="verify-otp" element={<OTPVerificationPage />} />
        <Route path="forgot-password" element={<div>Forgot Password Page</div>} />
        <Route path="reset-password" element={<div>Reset Password Page</div>} /> */}
      </Route>

      {/* Unauthorized Page */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Home — default landing after login */}
      <Route
        path="/home"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.DASHBOARD} />}
      >
        <Route index element={<AnalyticsPage />} />
      </Route>

      {/* Dashboard Routes - Protected for authenticated users */}
      <Route
        path="/dashboard"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.DASHBOARD} />}
      >
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="smart-home" element={<div className="p-8"><h1 className="text-2xl font-bold">Smart Home Dashboard</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        <Route index element={<Navigate to="/home" replace />} />
      </Route>

      {/* Pages Routes - Protected for authenticated users */}
      <Route
        path="/pages"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.PAGES} />}
      >
        <Route path="applications/kanban" element={<LanguageManagementPage />} />
        <Route path="applications/data-tables" element={<GenreManagementPage />} />
        <Route path="applications/calendar" element={<div className="p-8"><h1 className="text-2xl font-bold">Calendar</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        
        {/* Movie Routes */}
        <Route path="movies" element={<MovieManagementPage />} />
        <Route path="movies/create" element={<MovieCreatePage />} />
        <Route path="movies/:movieId/edit" element={<MovieEditPage />} />
        <Route path="movies/:movieId" element={<MovieDetailPage />} />

        {/* Series Routes (DramaType.SERIES) */}
        <Route path="series" element={<SeriesManagementPage />} />
        <Route path="series/create" element={<SeriesCreatePage />} />
        <Route path="series/:seriesId/edit" element={<SeriesEditPage />} />
        <Route path="series/:seriesId" element={<SeriesDetailPage />} />

        <Route path="music-videos" element={<MusicVideoManagementPage />} />
        <Route path="songs" element={<SongManagementPage />} />
        <Route path="artists" element={<ArtistManagementPage />} />
        {/* Playlists: user-owned in backend — no /api/admin/playlists */}
        <Route path="playlists/*" element={<PlaylistsUnavailablePage />} />
        <Route path="albums" element={<AlbumManagementPage />} />

        {/* Documentary Routes */}
        <Route path="documentaries" element={<DocumentaryManagementPage />} />
        <Route path="documentaries/create" element={<DocumentaryCreatePage />} />
        <Route
          path="documentaries/:documentaryId/edit"
          element={<DocumentaryEditPage />}
        />
        <Route
          path="documentaries/:documentaryId"
          element={<DocumentaryDetailPage />}
        />

        {/* Casts Routes */}
        <Route path="casts" element={<CastManagementPage />} />

        {/* Banner Routes */}
        <Route path="banners" element={<BannerManagementPage />} />

        {/* Trailer Routes */}
        <Route path="trailers" element={<TrailerManagementPage />} />

        {/* Splash Screen Routes */}
        <Route path="splash-screens" element={<SplashScreenManagementPage />} />

        {/* Payment Methods Routes */}
        <Route path="payment-methods" element={<PaymentMethodManagementPage />} />
        
        <Route path="ecommerce/products" element={<div className="p-8"><h1 className="text-2xl font-bold">Products</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        <Route path="ecommerce/orders" element={<div className="p-8"><h1 className="text-2xl font-bold">Orders</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
      </Route>

      {/* Admin Routes - Protected for SUPER_ADMIN and ADMIN only */}
      <Route
        path="/admin"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.ADMIN} />}
      >
        <Route path="users" element={<UserManagementPage />} />
        <Route path="users/tv-onboard" element={<TvUserOnboardingPage />} />
        <Route path="subscription-plans" element={<SubscriptionPlanManagementPage />} />
        <Route path="app-updates" element={<AppUpdateManagementPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Merchant Routes - Protected for MERCHANT and RESTAURANT_OWNER */}
      <Route
        path="/merchant"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.MERCHANT} />}
      >
        <Route path="products" element={<div className="p-8"><h1 className="text-2xl font-bold">My Products</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        <Route path="orders" element={<div className="p-8"><h1 className="text-2xl font-bold">My Orders</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
      </Route>

      {/* Docs Routes - Public for all authenticated users */}
      <Route
        path="/docs"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.DOCS} />}
      >
        <Route path="basic" element={<div className="p-8"><h1 className="text-2xl font-bold">Basic Documentation</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        <Route path="components" element={<div className="p-8"><h1 className="text-2xl font-bold">Components Documentation</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
        <Route path="changelog" element={<div className="p-8"><h1 className="text-2xl font-bold">Changelog</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
      </Route>

      {/* Profile & Settings - Protected for authenticated users */}
      <Route
        path="/profile"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.PROFILE} />}
      >
        <Route index element={<div className="p-8"><h1 className="text-2xl font-bold">My Profile</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
      </Route>

      <Route
        path="/settings"
        element={<ProtectedDashboardWrapper config={ROUTE_CONFIGS.SETTINGS} />}
      >
        <Route index element={<div className="p-8"><h1 className="text-2xl font-bold">Settings</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
      </Route>

      {/* Catch-all route - redirect to login for guests */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
  );
}

function createRouteConfig(config: Partial<RouteConfig>): RouteConfig {
  return {
    path: config.path || '/',
    component: config.component || (() => <div>Page</div>),
    exact: config.exact ?? false,
    accessLevel: config.accessLevel || 'public',
    allowedRoles: config.allowedRoles,
    title: config.title,
    description: config.description,
    requiresAuth: config.requiresAuth ?? false,
    layout: config.layout || 'default'
  };
}

/**
 * Route Configurations with Access Control
 * 
 * Defines access levels and allowed roles for each route section
 */
export const ROUTE_CONFIGS: Record<string, RouteConfig> = {
  // Public routes
  HOME: createRouteConfig({
    path: '/home',
    accessLevel: 'role-specific',
    allowedRoles: [
      'SUPER_ADMIN',
      'CONTENT_MANAGER',
      'USER_SUPPORT',
      'ANALYST',
      'MODERATOR',
      'ADMIN',
      'MERCHANT',
      'RESTAURANT_OWNER',
    ],
    title: 'Home',
    layout: 'dashboard',
    requiresAuth: true,
  }),

  // Auth routes
  LOGIN: createRouteConfig({
    path: '/auth/login',
    accessLevel: 'public',
    title: 'Sign In',
    layout: 'auth'
  }),

  // Dashboard routes - Authenticated users only
  DASHBOARD: createRouteConfig({
    path: '/dashboard',
    accessLevel: 'role-specific',
    allowedRoles: [
      'SUPER_ADMIN',
      'CONTENT_MANAGER',
      'USER_SUPPORT',
      'ANALYST',
      'MODERATOR',
      // legacy / existing
      'ADMIN',
      'MERCHANT',
      'RESTAURANT_OWNER',
    ],
    title: 'Dashboard',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Pages routes - All authenticated users
  PAGES: createRouteConfig({
    path: '/pages',
    accessLevel: 'authenticated',
    title: 'Pages',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Admin routes - SUPER_ADMIN and ADMIN only
  ADMIN: createRouteConfig({
    path: '/admin',
    accessLevel: 'role-specific',
    allowedRoles: [
      'SUPER_ADMIN',
      'CONTENT_MANAGER',
      'USER_SUPPORT',
      'ANALYST',
      'MODERATOR',
      // legacy
      'ADMIN',
    ],
    title: 'Admin Panel',
    layout: 'dashboard',
    requiresAuth: true
  }),

  ADMIN_USERS: createRouteConfig({
    path: '/admin/users',
    accessLevel: 'role-specific',
    allowedRoles: [
      'SUPER_ADMIN',
      'USER_SUPPORT',
      // legacy
      'ADMIN',
    ],
    title: 'User Management',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Merchant routes - MERCHANT and RESTAURANT_OWNER only
  MERCHANT: createRouteConfig({
    path: '/merchant',
    accessLevel: 'role-specific',
    allowedRoles: ['MERCHANT', 'RESTAURANT_OWNER'],
    title: 'Merchant Panel',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Docs routes - All authenticated users
  DOCS: createRouteConfig({
    path: '/docs',
    accessLevel: 'authenticated',
    title: 'Documentation',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Profile routes - All authenticated users
  PROFILE: createRouteConfig({
    path: '/profile',
    accessLevel: 'authenticated',
    title: 'My Profile',
    layout: 'dashboard',
    requiresAuth: true
  }),

  // Settings routes - All authenticated users
  SETTINGS: createRouteConfig({
    path: '/settings',
    accessLevel: 'authenticated',
    title: 'Settings',
    layout: 'dashboard',
    requiresAuth: true
  })
};